import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

interface GMBPhoto {
  name?: string;
  googleUrl?: string;
  sourceUrl?: string;
  thumbnailUrl?: string;
  description?: string;
  locationAssociation?: {
    category?: string;
  };
}

interface LandingGallerySectionProps {
  content: any;
  theme: any;
  businessInfo?: any;
  location?: any;
  workspace?: any;
  isStandalonePage?: boolean;
}

const LandingGallerySection = ({ content, location, isStandalonePage = false }: LandingGallerySectionProps) => {
  const { subdomain } = useParams();
  const navigate = useNavigate();
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [imageLoading, setImageLoading] = useState(false);

  // Check if we're on an actual subdomain (subdomain.localhost) vs testing route (/subdomain/:subdomain)
  const isOnSubdomain = () => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      // subdomain.localhost or subdomain.my-domain.com
      return parts.length >= 2 && parts[0] !== 'localhost' && parts[0] !== 'www';
    }
    return false;
  };

  const getBasePath = () => {
    return isOnSubdomain() ? '' : `/subdomain/${subdomain}`;
  };

  // Get computed theme colors from CSS variables for portal-rendered modals
  const getThemeColors = () => {
    const defaults = {
      bg: '#ffffff',
      bgSecondary: '#f5f5f5',
      text: '#000000',
      textSecondary: '#666666',
      borderColor: '#e0e0e0',
      primary: '#3b82f6',
    };

    if (typeof window === 'undefined') return defaults;
    const root = document.querySelector('.subdomain-landing-root');
    if (!root) return defaults;

    const styles = window.getComputedStyle(root);
    const bg = styles.getPropertyValue('--bg').trim();
    const bgSecondary = styles.getPropertyValue('--bg-secondary').trim();
    const text = styles.getPropertyValue('--text').trim();
    const textSecondary = styles.getPropertyValue('--text-secondary').trim();
    const borderColor = styles.getPropertyValue('--border-color').trim();
    const primary = styles.getPropertyValue('--primary').trim();

    return {
      bg: bg || defaults.bg,
      bgSecondary: bgSecondary || defaults.bgSecondary,
      text: text || defaults.text,
      textSecondary: textSecondary || defaults.textSecondary,
      borderColor: borderColor || defaults.borderColor,
      primary: primary || defaults.primary,
    };
  };

  // Helper to get high-quality URL from GMB photo (append =s800 for good quality)
  const getPhotoUrl = (photo: GMBPhoto | string): string => {
    if (typeof photo === 'string') {
      return photo;
    }
    // Prefer googleUrl, then sourceUrl, then thumbnailUrl
    const url = photo.googleUrl || photo.sourceUrl || photo.thumbnailUrl || '';
    if (!url) return '';
    // Remove any existing size parameter and add =s800 for good quality display
    return url.replace(/=s\d+$/, '') + '=s800';
  };

  // Helper to get full resolution URL for modal
  const getFullResUrl = (photo: GMBPhoto | string): string => {
    if (typeof photo === 'string') {
      return photo;
    }
    const url = photo.googleUrl || photo.sourceUrl || photo.thumbnailUrl || '';
    if (!url) return '';
    // Use =s1600 for full resolution in modal
    return url.replace(/=s\d+$/, '') + '=s1600';
  };

  // Build gallery images from location data
  const allImages = useMemo(() => {
    const galleryImages: { url: string; fullUrl: string }[] = [];

    // First, add photos from location.photos array (if available)
    if (location?.photos && Array.isArray(location.photos)) {
      location.photos.forEach((photo: GMBPhoto | string) => {
        const url = getPhotoUrl(photo);
        if (url) {
          galleryImages.push({
            url,
            fullUrl: getFullResUrl(photo),
          });
        }
      });
    }

    // Also add photos from media_items.photos (if available and not already included)
    if (location?.media_items?.photos && Array.isArray(location.media_items.photos)) {
      location.media_items.photos.forEach((photo: GMBPhoto) => {
        const url = getPhotoUrl(photo);
        if (url && !galleryImages.some(img => img.url === url)) {
          galleryImages.push({
            url,
            fullUrl: getFullResUrl(photo),
          });
        }
      });
    }

    // Fallback to content.images if provided (for custom galleries)
    if (galleryImages.length === 0 && content?.images && Array.isArray(content.images)) {
      content.images.forEach((image: any) => {
        const url = image.url || image;
        if (url) {
          galleryImages.push({
            url,
            fullUrl: url,
          });
        }
      });
    }

    return galleryImages;
  }, [location, content]);

  // Limit images to 12 on landing page, show all on standalone page
  const displayImages = isStandalonePage ? allImages : allImages.slice(0, 12);
  const hasMoreImages = !isStandalonePage && allImages.length > 12;

  // Set loading when image index changes
  useEffect(() => {
    if (selectedImageIndex !== null) {
      setImageLoading(true);
    }
  }, [selectedImageIndex]);

  // Navigation handlers for modal
  const goToPrevious = () => {
    if (selectedImageIndex !== null && selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  const goToNext = () => {
    if (selectedImageIndex !== null && selectedImageIndex < displayImages.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      goToPrevious();
    } else if (e.key === 'ArrowRight') {
      goToNext();
    }
  };

  // Don't render section if no images available
  if (allImages.length === 0) {
    return null;
  }

  const selectedImage = selectedImageIndex !== null ? displayImages[selectedImageIndex] : null;

  return (
    <section id="gallery" className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: "var(--primary)" }}
          >
            {content?.heading || "Gallery"}
          </h2>
          <div className="w-24 h-1 mx-auto" style={{ backgroundColor: "var(--primary)" }} />
          <p className="mt-6 text-lg text-gray-600 max-w-2xl mx-auto">
            {content?.description || "Take a look at our work and facilities"}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayImages.map((image, index) => (
            <div
              key={index}
              className="group relative overflow-hidden rounded-lg shadow-md cursor-pointer hover:shadow-xl transition-shadow duration-300"
              onClick={() => setSelectedImageIndex(index)}
            >
              <img
                src={image.url}
                alt={`Gallery image ${index + 1}`}
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                loading="lazy"
              />
            </div>
          ))}
        </div>

        {hasMoreImages && (
          <div className="text-center mt-12">
            <Button
              onClick={() => navigate(`${getBasePath()}/gallery`)}
              style={{ backgroundColor: "var(--primary)", color: "var(--primary-foreground)" }}
              className="hover:opacity-90"
            >
              View All Photos ({allImages.length})
            </Button>
          </div>
        )}

        <Dialog open={selectedImageIndex !== null} onOpenChange={() => setSelectedImageIndex(null)}>
          <DialogContent
            className="gallery-modal max-w-4xl max-h-[90vh] !bg-transparent !border-none !p-0"
            onKeyDown={handleKeyDown}
          >
            {selectedImage && (() => {
              const colors = getThemeColors();
              return (
                <div
                  style={{
                    backgroundColor: colors.bg,
                    color: colors.text,
                    borderRadius: '12px',
                    padding: '16px',
                    border: `1px solid ${colors.borderColor}`,
                  }}
                >
                  {/* Image Container with Loader */}
                  <div
                    className="relative flex items-center justify-center rounded-lg overflow-hidden"
                    style={{
                      backgroundColor: colors.bgSecondary,
                      minHeight: '200px',
                    }}
                  >
                    {/* Loading Spinner */}
                    {imageLoading && (
                      <div className="absolute inset-0 flex items-center justify-center z-10">
                        <Loader2
                          className="w-10 h-10 animate-spin"
                          style={{ color: colors.primary }}
                        />
                      </div>
                    )}

                    {/* Previous Button */}
                    {selectedImageIndex !== null && selectedImageIndex > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          goToPrevious();
                        }}
                        className="absolute left-3 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full transition-colors"
                        style={{
                          backgroundColor: colors.bg,
                          color: colors.text,
                          border: `1px solid ${colors.borderColor}`,
                        }}
                        aria-label="Previous image"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                    )}

                    <img
                      src={selectedImage.fullUrl}
                      alt="Gallery preview"
                      className={`max-w-full max-h-[70vh] w-auto h-auto object-contain transition-opacity duration-300 ${
                        imageLoading ? 'opacity-0' : 'opacity-100'
                      }`}
                      onLoad={() => setImageLoading(false)}
                      onError={() => setImageLoading(false)}
                    />

                    {/* Next Button */}
                    {selectedImageIndex !== null && selectedImageIndex < displayImages.length - 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          goToNext();
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full transition-colors"
                        style={{
                          backgroundColor: colors.bg,
                          color: colors.text,
                          border: `1px solid ${colors.borderColor}`,
                        }}
                        aria-label="Next image"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                    )}
                  </div>

                  {/* Image Counter */}
                  <div
                    className="mt-4 text-center text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    {(selectedImageIndex ?? 0) + 1} of {displayImages.length}
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default LandingGallerySection;
